import { faWarning } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@vapor/material'
import { t } from 'i18next'
import React, { useEffect, useMemo, useState } from 'react'
import { PricingType } from 'src/api/api.generated'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { ReservationCartRowAdditionalService, ReservationNewAdditionalService } from '../../form/reservation-new.type'
import AdditionalServiceOption from './additional-service-option'
import { calculateAdditionalServicePrice } from 'src/utils/additional-services-utils'

type AdditionalServicesCreationModalProps = {
  open: boolean
  handleClose: () => void
  handleAddService: (additionalService: AdditionalServiceOption[]) => void
  additionalService: ReservationNewAdditionalService
  cartResourceTypes: AdditionalServiceOption[]
}

export type AdditionalServiceOption = ReservationCartRowAdditionalService & {
  cartId: string
  resourceTypeName: string
  resourceTypeCode?: string
  childrenOccupancy: {
    childId: string
    quantity: number
  }[]
}

export default function AdditionalServicesCreationModal(props: AdditionalServicesCreationModalProps) {
  const { open, additionalService, cartResourceTypes, handleClose, handleAddService } = props
  const localizeString = useLocalizeString()
  const [additionalServiceQuantity, setAdditionalServiceQuantity] = useState<AdditionalServiceOption[]>([])

  const [showNoChangesMessage, setShowNoChangesMessage] = useState(false)

  const thereAreChanges = useMemo(() => {
    return JSON.stringify(cartResourceTypes) !== JSON.stringify(additionalServiceQuantity)
  }, [cartResourceTypes, additionalServiceQuantity])

  useEffect(() => {
    if (showNoChangesMessage && thereAreChanges) {
      setShowNoChangesMessage(false)
    }
  }, [thereAreChanges])

  useEffect(() => {
    const tmpAdditionalServiceQuantity: AdditionalServiceOption[] = []
    cartResourceTypes.forEach((resourceType) => tmpAdditionalServiceQuantity.push({ ...resourceType }))
    setAdditionalServiceQuantity(tmpAdditionalServiceQuantity)
  }, [JSON.stringify(cartResourceTypes), setAdditionalServiceQuantity])

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="column" spacing={-1}>
            <Typography variant="titleSmall">
              <b>{localizeString(additionalService.additionalService.name)}</b>
            </Typography>
            <Typography variant="titleXSmall">({t(`pms.booking.reservation_new.additional_services_pricing_type.${additionalService.additionalService.pricingType}`)})</Typography>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: 'var(--AzureishWhite)', padding: '8px !important' }}>
        <Stack direction="column" spacing={1}>
          {cartResourceTypes.map((resourceType) => {
            const tmpAdditionalService = additionalServiceQuantity.find((as) => as.cartId === resourceType.cartId)

            const additionalServiceFinalPrice = calculateAdditionalServicePrice({
              adults: resourceType.adults,
              children: resourceType.childrenOccupancy,
              price: additionalService.additionalService.price,
              pricingType: additionalService.additionalService.pricingType,
              days: resourceType.nights,
              automaticForAdults: additionalService.additionalService.automaticForAdults,
              automaticForChildren: additionalService.additionalService.automaticForChildren,
              quantity: 1,
              childPrices: additionalService.additionalService.childServicePrices,
              priceZero: additionalService.priceZero,
            })

            return (
              <React.Fragment key={resourceType.cartId}>
                <AdditionalServiceOption
                  resourceTypeName={resourceType.resourceTypeCode ?? resourceType.resourceTypeName}
                  quantity={tmpAdditionalService?.quantity ?? null}
                  maxQuantity={tmpAdditionalService?.availability ?? null}
                  onQuantityChanges={(newQuantity) => {
                    const index = additionalServiceQuantity.findIndex((as) => as.cartId === resourceType.cartId)
                    if (index >= 0) {
                      const tmp = [...additionalServiceQuantity]

                      const canHaveQuantity = additionalService.additionalService.pricingType === PricingType.PER_NIGHT || additionalService.additionalService.pricingType === PricingType.PER_STAY

                      tmp[index] = {
                        ...additionalServiceQuantity[index],
                        quantity: !canHaveQuantity ? undefined : newQuantity,
                      }
                      setAdditionalServiceQuantity(tmp)
                    }
                  }}
                  isAdded={tmpAdditionalService?.added ?? false}
                  onAddedChanges={(newAdded) => {
                    const index = additionalServiceQuantity.findIndex((as) => as.cartId === resourceType.cartId)
                    if (index >= 0) {
                      const tmp = [...additionalServiceQuantity]
                      tmp[index] = {
                        ...additionalServiceQuantity[index],
                        added: newAdded,
                      }
                      setAdditionalServiceQuantity(tmp)
                    }
                  }}
                  pricingType={additionalService.additionalService.pricingType}
                  price={additionalService.additionalService.price}
                  finalPrice={additionalServiceFinalPrice}
                  currency={additionalService.additionalService.currency}
                  adults={resourceType.adults}
                  nights={resourceType.nights}
                  children={resourceType.children}
                  additionalService={additionalService.additionalService}
                />
              </React.Fragment>
            )
          })}
          {showNoChangesMessage && (
            <Stack direction="row" spacing={1} pr={2} pl={2}>
              <FontAwesomeIcon icon={faWarning} color="orange" style={{ width: 18, height: 18 }} />
              <Typography>{t('pms.booking.reservation_new.additional_service_dialog.no_changes_message')}</Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={handleClose}>
          {t('commons.cancel')}
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            if (thereAreChanges) {
              handleAddService(additionalServiceQuantity)
            } else {
              setShowNoChangesMessage(true)
            }
          }}
        >
          {t('pms.booking.reservation_new.additional_service_dialog.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
